/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import qs from 'qs';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const useDownloadRecommendations = (
  params: Type.DownloadRecommendationReq,
) => {
  const apiUrl = `/answer/admin/api/download-recommendations?${qs.stringify(
    params,
    { skipNulls: true },
  )}`;
  const { data, error, mutate } = useSWR<
    Type.ListResult<Type.DownloadRecommendationItem>,
    Error
  >([apiUrl], request.instance.get);
  return {
    data,
    isLoading: !data && !error,
    error,
    mutate,
  };
};

export const rebuildDownloadRecommendations = () => {
  return request.post<{ indexed_answers: number }>(
    '/answer/admin/api/download-recommendations/rebuild',
  );
};

export const inferDownloadRecommendation = (id: number) => {
  return request.post<Type.DownloadRecommendationInferResp>(
    '/answer/admin/api/download-recommendations/infer',
    { id },
  );
};

export const markDownloadRecommendationsPending = (ids: number[]) => {
  return request.post('/answer/admin/api/download-recommendations/pending', {
    ids,
  });
};

export const unpublishDownloadRecommendation = (id: number) => {
  return request.post('/answer/admin/api/download-recommendations/unpublish', {
    id,
  });
};

export const deleteDownloadRecommendation = (id: number) => {
  return request.delete('/answer/admin/api/download-recommendations', { id });
};

export const createManualDownloadRecommendation = (
  params: Type.DownloadRecommendationManualCreateParams,
) => {
  const form = new FormData();
  form.append('software_name', params.software_name);
  form.append('platform', params.platform);
  if (params.tag_display_name) {
    form.append('tag_display_name', params.tag_display_name);
  }
  if (params.tag_slug_name) {
    form.append('tag_slug_name', params.tag_slug_name);
  }
  if (params.recommend_reason) {
    form.append('recommend_reason', params.recommend_reason);
  }
  if (params.official_url) {
    form.append('official_url', params.official_url);
  }
  if (params.external_download_url) {
    form.append('external_download_url', params.external_download_url);
  }
  if (params.package_version) {
    form.append('package_version', params.package_version);
  }
  if (params.package_release_note) {
    form.append('package_release_note', params.package_release_note);
  }
  if (params.detail_markdown) {
    form.append('detail_markdown', params.detail_markdown);
  }
  if (params.file) {
    form.append('file', params.file);
  }
  return request.post<Type.DownloadRecommendationItem>(
    '/answer/admin/api/download-recommendations/manual',
    form,
  );
};

export const uploadDownloadRecommendationPackage = (
  params: Type.DownloadRecommendationUploadParams,
) => {
  const form = new FormData();
  form.append('id', String(params.id));
  if (params.file) {
    form.append('file', params.file);
  }
  if (params.official_url) {
    form.append('official_url', params.official_url);
  }
  if (params.external_download_url) {
    form.append('external_download_url', params.external_download_url);
  }
  if (params.tag_display_name) {
    form.append('tag_display_name', params.tag_display_name);
  }
  if (params.tag_slug_name) {
    form.append('tag_slug_name', params.tag_slug_name);
  }
  if (params.package_version) {
    form.append('package_version', params.package_version);
  }
  if (params.package_release_note) {
    form.append('package_release_note', params.package_release_note);
  }
  if (params.detail_markdown) {
    form.append('detail_markdown', params.detail_markdown);
  }
  return request.post<Type.DownloadRecommendationItem>(
    '/answer/admin/api/download-recommendations/package',
    form,
  );
};
